import React, { useState, useMemo } from "react";
import { colors, css, frames, glyphs, mediaQueriesNumbers, useMediaQuery } from "../../../../../Styles";
import { PageHeader, FilterPopup, AicoConnection, TableLayout, ReactiveTable, NewButtonList, LoadingSpinner } from "../../../../../Components";
import { ListData_ValueTypes, TableStyles_Types, ButtonTypes } from "../../../../../Definitions";
import { ColumnFiltersState, useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel, getPaginationRowModel } from "@tanstack/react-table";
import { CONTROLLER_LOG_AICO, LanControllerLogAicoArgs, LanControllerLogAicoResponse } from "../../../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/controllerlogaico";
import { useColumnConstructor } from "../../../../../Components/Tables/_Tables/useColumnConstructor";
import { useAicoConnection } from "../../../../../Components/ToolsComponents/_ToolsComponents/aicoConnectionContext";
import globalFilter from "../../../../../Components/Tables/_Tables/useGlobalFilter";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";

const levelMap: Record<number, string> = {
  0: "Error",
  1: "Warning",
  2: "Info",
  3: "Debug",
};
const typeMap: Record<number, string> = {
  1: "System",
  2: "Motor",
  3: "Animal",
  4: "Server",
};

const getLevelOptions = Object.entries(levelMap).map(([key, value]) => ({label: value, id: +key}));

const getTypeOptions = Object.entries(typeMap).map(([key, value]) => ({ label: value, id: +key }));

const accessorData = [
  { headerValue: "Time", accessor: "timestamp", type: ListData_ValueTypes.Date, sort: true, width: "200px", displaySeconds: true },
  { headerValue: "Type", accessor: "type", type: ListData_ValueTypes.List, sort: true, width: "120px", filter: true, options: getTypeOptions },
  { headerValue: "Level", accessor: "level", type: ListData_ValueTypes.List, sort: true, width: "120px", filter: true, options: getLevelOptions },
  { headerValue: "Message", accessor: "message", type: ListData_ValueTypes.String, sort: true },
  { headerValue: "Lancontroller", accessor: "lan_controller", type: ListData_ValueTypes.String, sort: true, width: "200px" },
  { headerValue: "IO", accessor: "device", type: ListData_ValueTypes.String, sort: true, width: "120px" },
  { headerValue: "Terminal", accessor: "terminals", type: ListData_ValueTypes.String, sort: true, width: "120px" },
  { headerValue: "Animals", accessor: "animals", type: ListData_ValueTypes.String, sort: true, width: "120px" },
]

export default function LogViewBlock() {
  const { t } = useTranslation();
  const mobileXs = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`); 

  const { state: connectionState } = useAicoConnection();


  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const { data, loading, error, refetch } = useQuery<LanControllerLogAicoResponse>( CONTROLLER_LOG_AICO, {
    variables: connectionState.deviceConnection
      ? { domain: connectionState.deviceConnection.domain,
          input: { ids: [connectionState.deviceConnection.lanControllerId], offset: 0, fetchAmount: 501 },
        } : undefined,
    skip: !connectionState,
    fetchPolicy: "network-only", 
    notifyOnNetworkStatusChange: true,
  });


  // const transformMessage = (msg: string): string => {
  //   if (typeof msg !== "string") return String(msg ?? "");
  //   return msg
  //     .replace(/\bF(\d+)/g, "feedcurve $1")
  //     .replace(/\bM(\d+)/g, "Milestone $1")
  //     .replace(/\bD(\d+)/g, "Day $1");
  // };

  const transformMessage = (msg: string): string => {
    if (typeof msg !== "string") return String(msg ?? "");

    const match = msg.match(/^F(\d+):M(\d+):D(\d+)$/);
    if (!match) return msg;

    const [, f, m, d] = match;
    return `Feedcurve ${f} Milestone ${m} Day ${d}`;
  };

 const tableData = useMemo(() => {
  if (!data?.controller?.controllerLogsAico) return [];
  return data.controller.controllerLogsAico.map((log: LanControllerLogAicoArgs) => ({
    id: log.lan_controller_log_id,
    timestamp: new Date(log.timestamp * 1000),
    level: { label: levelMap[log.level], id: log.level },
    type: { label: typeMap[log.type], id: log.type },
    message: transformMessage(log.message),
    terminals: log.port ? `Port: ${log.port}` : "",
    animals: log.fk_animals ?? "",
    lan_controller: log.lan_controller_cpu_id ? `CPU: ${log.lan_controller_cpu_id}` : "",
    device: log.device_id ? `Device: ${log.device_id}` : "",
  }));
}, [data]);

  const columns = useColumnConstructor({
    accessorData,
    tableStyle: TableStyles_Types.primaryTable,
    canEdit: false,
  });

  const pageSize = 500;

  const totalLogs = data?.controller?.controllerLogsAico.length ?? 0;

  // const totalPages = Math.ceil(totalLogs / pageSize);
  const totalPages = -1

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    manualPagination: true,
    globalFilterFn: globalFilter,
    pageCount: totalPages,
    state: {
      globalFilter: globalFilterValue,
      columnFilters,
    },
    initialState: {
      sorting: [{ id: "timestamp", desc: true }],
      pagination: { pageSize },
    },
  });

  return (
    <frames.Page_inner>
        <PageHeader topHeader='Aico Log' subHeader='Log'/>

      <frames._page_inner_content>
        <TableLayout
          fillScreenTable
          searchbBarArea={mobileXs ? undefined : <AicoConnection inTable/>}
          buttonArea={<>
            {mobileXs && <AicoConnection inTable/>}
            <FilterPopup accessorData={accessorData} table={table} />
            <NewButtonList
              contents={["fetch"]}
              disabled={[false]}
              buttonType={ButtonTypes.tableButton}
              startEnhancers={[ glyphs.Fetch({}) ]}
              onClick={(i => { 
                if (i === 0) {
                  table.firstPage();
                  refetch({
                    input: {
                      ids: [connectionState.lanControllerId ?? 0],
                      fetchAmount: 501,
                      offset: 0,
                    },
                  });
                } 
              })}
            /></>
          }
          tableArea={<>
            {loading && <div style={{padding: "20px", display: "flex", justifyContent: "center"}}><LoadingSpinner/></div>}
            {(!loading && (error ||tableData.length === 0)) && <p style={{ ...css.emptyPen, textAlign: "center", padding: "20px" }}>{t("noData", { defaultValue: "no data" })}</p>}
            {!loading && !error && tableData.length !== 0 &&
              <ReactiveTable
                table={table}
                tableType={TableStyles_Types.primaryTable}
                emptyText=""
                renderFooter={false}
                loading={loading}
                minRows={30}
              />
            }
          </>}
          paginationBtns={{
            onPrev: () => {
              if (table.getCanPreviousPage()) {
                refetch({
                  input: {
                    ids: [connectionState.lanControllerId ?? 0],
                    fetchAmount: 501,
                    offset: 500 * (table.getState().pagination.pageIndex - 1),
                  },
                });
                table.previousPage();
              }
            },
            onNext: () => {
              if (table.getCanNextPage() && (data?.controller.controllerLogsAico.length && data.controller.controllerLogsAico.length > 500)) {
                refetch({
                  input: {
                    ids: [connectionState.lanControllerId ?? 0],
                    fetchAmount: 501,
                    offset: 500 * (table.getState().pagination.pageIndex + 1),
                  },
                });
                table.nextPage();
              }
            },
            canNextPage: !table.getCanNextPage(),
            canPreviousPage: !table.getCanPreviousPage(),
            thisPageIndex: table.getState().pagination.pageIndex + 1,
            pageCount: table.getPageCount()
          }}
        />
      </frames._page_inner_content>
    </frames.Page_inner>
  );
}